export type Lang = 'en' | 'es'

// Shared, language-agnostic constants.
export const ORDER_URL = 'https://cash.app/$tartahtx'

export const translations = {
  en: {
    seo: {
      title:
        'TARTA — Basque Cheesecake in Houston | Order & Saturday Market Pickup',
      description:
        'TARTA bakes small-batch Basque cheesecake in Houston, TX. Order online any time — Pistachio, Biscoff, Oreo, Chocolate & Manchego — and pick up fresh every Saturday at the Memorial Villages Farmers Market.',
      keywords:
        'Basque cheesecake, Houston cheesecake, San Sebastián cheesecake, burnt cheesecake, Memorial Villages Farmers Market, TARTA, order cheesecake Houston, small batch dessert',
      ogAlt:
        'An open TARTA box revealing a deeply caramelized Basque cheesecake',
    },
    order: {
      label: 'Order Now',
      sub: 'Pickup · Memorial Villages Farmers Market',
    },
    header: {
      tagline: 'A slice of heaven',
      navCheesecake: 'Our Tartas',
      navOccasions: 'Flavors',
      navEnquiries: 'About',
      navFaq: 'FAQ',
      navReserve: 'Order',
    },
    hero: {
      banner: 'Now at the Memorial Villages Farmers Market',
      eyebrow: 'Order Now',
      sideLeft: 'Signature Basque Cheesecake',
      sideRight: 'Houston · Texas · MMXXVI',
      title1: 'A Slice',
      title2: 'of',
      title3: 'Heaven',
      tagline: "The Basque cheesecake you didn't know you were missing.",
      plate: 'Plate Nº 001',
      plateLabel: 'Signature',
      subcopy:
        'Creamy, rich, and baked in its own beautiful box. TARTA brings the most beloved dessert of San Sebastián straight to Houston, Texas. Small batch. Made with love. Built to make you feel something.',
      secondary:
        'Order any time and pick up fresh every Saturday at the Memorial Villages Farmers Market. Limited batches, five flavors, one slice of heaven.',
      ctaReserve: 'See flavors & pickup',
      ctaFollow: 'Follow @tartahtx',
    },
    marquee: [
      'Caramelized',
      'Custard-Soft',
      'Small-Batch',
      'Hand-Crafted',
      'Houston · Texas',
      'San Sebastián Roots',
      'Limited Flavors',
      'Made with Love',
    ],
    about: {
      eyebrow: 'About TARTA',
      title1: 'Some obsessions are worth',
      title2: 'following to the source.',
      pullQuote: 'That first bite stayed with me for years.',
      paragraphs: [
        'It started in 2021 with a single bite. A creamy, almost molten cheesecake unlike anything I had ever tasted. I was in Mexico, but the dessert came from somewhere far away. A small city in northern Spain called San Sebastián, the birthplace of the Basque cheesecake.',
        'In 2025, I followed that memory across the ocean. My husband and I traveled to San Sebastián, where I enrolled in a professional gastronomy course to learn the craft from the place it was born. Then we made our way to Madrid, tasting the best Basque cheesecakes the city had to offer, and I knew mine belonged right there with them.',
        'I came back to Texas with a recipe, a dream, and a clear purpose: to bring this extraordinary dessert to the north of Houston, where nothing like it existed.',
        'My name is Day Díaz. I am a Venezuelan immigrant, a mother of two, and someone who has always believed that food is one of the most powerful ways to connect with people. Baking has always felt like a love language to me. A way to reach someone\u2019s heart through something warm, intentional, and made with care.',
        'TARTA was born from curiosity, from a relentless search for that perfect bite, and from the desire to share it with the people of this beautiful state I now call home.',
        'Every cheesecake is baked in its own box, made in small batches, and crafted to feel like exactly what it is. A slice of heaven.',
      ],
      signature: 'Day Díaz',
      signatureRole: 'Founder',
    },
    product: {
      eyebrow: 'Our Tartas',
      title1: 'This is not',
      title2: 'what you expect.',
      intro:
        'The Basque tarta is deliberately burnt on the outside and impossibly creamy on the inside. It breaks every rule and wins every time. No crust. No water bath. Just pure, cloud-like texture that melts the moment it touches your lips. Each TARTA is baked and served in its own signature box, made in small batches, and crafted to be as beautiful as it is delicious.',
      features: [
        {
          eyebrow: 'The Outside',
          title: 'Burnt Top',
          copy: 'Deliberately caramelized to a deep, bittersweet crown. The rule-breaking signature of San Sebastián.',
        },
        {
          eyebrow: 'The Inside',
          title: 'Cloud-Like Center',
          copy: 'No crust. No water bath. Just pure, custard-soft texture that melts the moment it touches your lips.',
        },
        {
          eyebrow: 'The Box',
          title: 'Baked to Give',
          copy: 'Baked and served in its own signature box, made in small batches, and as beautiful as it is delicious.',
        },
      ],
    },
    flavors: {
      eyebrow: 'Available Flavors',
      title1: 'Five Ways to',
      title2: 'Fall in Love.',
      list: [
        {
          name: 'Pistachio',
          copy: 'Crowned with crushed pistachios. Delicate, nutty, and elegant.',
          img: 'flavorPistachio',
        },
        {
          name: 'Biscoff',
          copy: 'Warm, spiced caramel cookie notes over our signature texture. Dangerously good.',
          img: 'flavorLotus',
        },
        {
          name: 'Oreo',
          copy: 'A classic reimagined in cocoa. Creamy, dreamy, and impossible to forget.',
          img: 'flavorOreo',
        },
        {
          name: 'Chocolate',
          copy: 'Deep, velvety chocolate folded into every bite. Pure indulgence.',
          img: 'flavorChocolate',
        },
        {
          name: 'Manchego',
          copy: 'Sweet meets savory in the most unexpected and unforgettable way.',
          img: 'flavorManchego',
        },
      ],
      footnote:
        'All five flavors are baked fresh in limited batches each week. Order any time and pick up Saturdays at the market.',
    },
    sensory: {
      eyebrow: 'The Vessel',
      title1: 'Made to be',
      title2: 'Given.',
      copy: 'Each cheesecake arrives in a hand-finished round box, embossed with the TARTA mark and lined with care. A vessel meant to be opened slowly, because the unboxing is part of the gift.',
      specs: [
        { l: 'Material', v: 'Embossed paperboard' },
        { l: 'Finish', v: 'Cocoa & cream foil' },
        { l: 'Form', v: 'Hand-finished round' },
        { l: 'Detail', v: 'A slice of heaven' },
      ],
      detailNo: 'Detail Nº 002',
      detailLabel: 'The Box',
    },
    waitlist: {
      invitation: 'stay in the loop',
      title1: 'Flavor & Market',
      title2: 'Updates',
      copy: 'Order any time and pick up Saturdays at the market. Leave your details to hear about weekly flavors, new market dates and the occasional surprise drop, straight to your inbox.',
      benefits: [
        'Weekly flavor lineup',
        'New market & pop-up dates',
        'Surprise drops',
      ],
      labelName: 'First Name',
      labelEmail: 'Email',
      labelPhone: 'Phone Number',
      labelCity: 'City / Area',
      labelFlavor: 'Favorite Flavor',
      placeholderName: 'First name',
      placeholderEmail: 'you@example.com',
      placeholderPhone: '(optional)',
      placeholderCity: 'Houston, Memorial…',
      flavorOptions: [
        'Pistachio',
        'Biscoff',
        'Oreo',
        'Chocolate',
        'Manchego',
        'Surprise me',
      ],
      flavorPlaceholder: 'Select a flavor',
      optional: 'Optional',
      required: 'Required',
      invalidEmail: 'Invalid email',
      submit: 'Keep me updated',
      footnote:
        'By signing up you agree to receive updates from TARTA. We respect your privacy and will never share your information.',
      confirmed: 'You\u2019re in',
      welcome: 'Thank you,',
      thanks:
        'You\u2019ll hear from us about weekly flavors and market dates. In the meantime, order any time and pick up Saturday at the market. One bite closer to heaven.',
    },
    localLaunch: {
      eyebrow: 'Find Us',
      title1: 'Every Saturday in',
      title2: 'Houston.',
      copy: 'TARTA pops up every Saturday at the Memorial Villages Farmers Market in Houston. Order any time online, then come pick up your cheesecake fresh at the market.',
      points: [
        { label: 'Origin', value: 'San Sebastián, Spain' },
        { label: 'Crafted in', value: 'Houston, Texas' },
        { label: 'Production', value: 'Small-batch' },
        { label: 'Every Saturday', value: 'Memorial Villages Farmers Market' },
      ],
    },
    preorder: {
      eyebrow: 'Order & Pickup',
      title1: 'Order Any Time.',
      title2: 'Pick Up Saturdays.',
      copy: 'Place your order online whenever you like and pick up your cheesecake fresh at the Memorial Villages Farmers Market every Saturday. Baked in limited batches, so order ahead to make sure your flavor is waiting for you.',
      marketLabel: 'Pickup Location',
      marketName: 'Memorial Villages Farmers Market',
      address1: '10840 Beinhorn Rd',
      address2: 'Houston, TX 77024',
      hoursLabel: 'Pickup Hours',
      hoursDay: 'Saturdays',
      hoursTime: '9:00 AM to 1:00 PM',
      cta: 'Get flavor updates',
      note: 'Limited batches each week. Order ahead so your flavor is reserved.',
    },
    instagram: {
      eyebrow: '@tartahtx',
      title1: 'Follow the',
      title2: 'First Chapter.',
      cta: 'Follow @tartahtx',
      posts: ['The first bake', 'The signature box', 'A slice of heaven'],
    },
    faq: {
      eyebrow: 'Enquiries',
      title1: 'Questions,',
      title2: 'Answered.',
      items: [
        {
          q: 'How do I order?',
          a: 'Order online any time using the Order Now button. Choose your flavor, place your order, and pick it up fresh at the market on Saturday.',
        },
        {
          q: 'Where do I pick up?',
          a: 'At the Memorial Villages Farmers Market, 10840 Beinhorn Rd, Houston, TX 77024, every Saturday from 9:00 AM to 1:00 PM.',
        },
        {
          q: 'What flavors do you offer?',
          a: 'Pistachio, Biscoff, Oreo, Chocolate and Manchego, all baked fresh in limited batches each week.',
        },
        {
          q: 'Do I have to order ahead?',
          a: 'You can order online any time, and we recommend it so your flavor is reserved. Batches are limited and popular flavors sell out.',
        },
        {
          q: 'How do I stay updated?',
          a: 'Sign up for flavor and market updates below, and follow @tartahtx on Instagram.',
        },
      ],
    },
    finalCTA: {
      edition: 'a slice of heaven',
      title1: 'One Bite',
      title2: 'Closer',
      title3: 'to Heaven',
      copy1: 'Limited flavors. Small batches. Made with love.',
      copy2: 'Order now and pick up Saturday at the market.',
      cta: 'Stay in the loop',
    },
    footer: {
      tagline: 'A slice of heaven',
      quote: 'One bite closer to heaven.',
      origin: 'Where',
      originValue: 'Houston, TX',
      originSub: 'Saturdays at the Market',
      follow: 'Follow',
      email: 'tarta@tartahtx.com',
      copyright: 'TARTA. All rights reserved.',
      crafted: 'Handcrafted in small batches in Houston, TX',
      links: ['Privacy Policy', 'Terms & Conditions', 'Contact'],
    },
  },

  es: {
    seo: {
      title:
        'TARTA — Cheesecake Vasco en Houston | Ordena y Recoge los Sábados',
      description:
        'TARTA hornea cheesecake vasco en lotes pequeños en Houston, TX. Ordena en línea cuando quieras — Pistacho, Biscoff, Oreo, Chocolate y Manchego — y recoge fresco cada sábado en el Memorial Villages Farmers Market.',
      keywords:
        'cheesecake vasco, cheesecake Houston, cheesecake San Sebastián, tarta vasca, Memorial Villages Farmers Market, TARTA, ordenar cheesecake Houston, postre lote pequeño',
      ogAlt:
        'Una caja TARTA abierta que revela un cheesecake vasco profundamente caramelizado',
    },
    order: {
      label: 'Ordena Ahora',
      sub: 'Recogida · Memorial Villages Farmers Market',
    },
    header: {
      tagline: 'Un pedacito de cielo',
      navCheesecake: 'Nuestras Tartas',
      navOccasions: 'Sabores',
      navEnquiries: 'Historia',
      navFaq: 'Preguntas',
      navReserve: 'Ordenar',
    },
    hero: {
      banner: 'Ya en el Memorial Villages Farmers Market',
      eyebrow: 'Ordena Ahora',
      sideLeft: 'Cheesecake Vasco de Autor',
      sideRight: 'Houston · Texas · MMXXVI',
      title1: 'Un Pedacito',
      title2: 'de',
      title3: 'Cielo',
      tagline: 'El cheesecake vasco que no sabías que te faltaba.',
      plate: 'Plato Nº 001',
      plateLabel: 'De Autor',
      subcopy:
        'Cremoso, intenso y horneado en su propia y hermosa caja. TARTA trae el postre más querido de San Sebastián directo a Houston, Texas. Lotes pequeños. Hecho con amor. Creado para hacerte sentir algo.',
      secondary:
        'Ordena cuando quieras y recoge tu cheesecake fresco cada sábado en el Memorial Villages Farmers Market. Lotes limitados, cinco sabores, un pedacito de cielo.',
      ctaReserve: 'Ver sabores y recogida',
      ctaFollow: 'Síguenos @tartahtx',
    },
    marquee: [
      'Caramelizado',
      'Cremoso',
      'En Lotes Pequeños',
      'Hecho a Mano',
      'Houston · Texas',
      'Raíces de San Sebastián',
      'Sabores Limitados',
      'Hecho con Amor',
    ],
    about: {
      eyebrow: 'Historia de TARTA',
      title1: 'Hay obsesiones que vale la',
      title2: 'pena seguir hasta su origen.',
      pullQuote: 'Aquel primer bocado se quedó conmigo durante años.',
      paragraphs: [
        'Comenzó en 2021 con un solo bocado. Un cheesecake cremoso, casi líquido, como nada que hubiera probado antes. Estaba en México, pero el postre venía de muy lejos. De una pequeña ciudad en el norte de España llamada San Sebastián, la cuna del cheesecake vasco.',
        'En 2025, seguí ese recuerdo al otro lado del océano. Mi esposo y yo viajamos a San Sebastián, donde me inscribí en un curso profesional de gastronomía para aprender el oficio en el lugar donde nació. Luego fuimos a Madrid, probando los mejores cheesecakes vascos de la ciudad, y supe que el mío pertenecía justo ahí, entre ellos.',
        'Regresé a Texas con una receta, un sueño y un propósito claro: traer este postre extraordinario al norte de Houston, donde no existía nada igual.',
        'Mi nombre es Day Díaz. Soy una inmigrante venezolana, madre de dos, y alguien que siempre ha creído que la comida es una de las formas más poderosas de conectar con las personas. Hornear siempre ha sido para mí un lenguaje de amor. Una manera de llegar al corazón de alguien con algo cálido, intencional y hecho con cuidado.',
        'TARTA nació de la curiosidad, de la búsqueda incansable de aquel bocado perfecto, y del deseo de compartirlo con la gente de este hermoso estado que ahora llamo hogar.',
        'Cada cheesecake se hornea en su propia caja, en lotes pequeños, y se elabora para sentirse exactamente como lo que es. Un pedacito de cielo.',
      ],
      signature: 'Day Díaz',
      signatureRole: 'Fundadora',
    },
    product: {
      eyebrow: 'Nuestras Tartas',
      title1: 'Esto no es',
      title2: 'lo que esperas.',
      intro:
        'La tarta vasca está deliberadamente tostada por fuera e imposiblemente cremosa por dentro. Un postre que rompe todas las reglas y gana siempre. Sin base. Sin baño maría. Solo una textura pura, como una nube, que se derrite en el momento en que toca tus labios. Cada TARTA se hornea y se sirve en su propia caja de autor, en lotes pequeños, y se elabora para ser tan hermosa como deliciosa.',
      features: [
        {
          eyebrow: 'El Exterior',
          title: 'Corona Tostada',
          copy: 'Caramelizada a propósito hasta una corona profunda y agridulce. La firma rompe-reglas de San Sebastián.',
        },
        {
          eyebrow: 'El Interior',
          title: 'Centro de Nube',
          copy: 'Sin base. Sin baño maría. Solo textura pura, suave como natilla, que se derrite al tocar tus labios.',
        },
        {
          eyebrow: 'La Caja',
          title: 'Horneada para Regalar',
          copy: 'Horneada y servida en su propia caja de autor, en lotes pequeños y tan hermosa como deliciosa.',
        },
      ],
    },
    flavors: {
      eyebrow: 'Sabores Disponibles',
      title1: 'Cinco Formas de',
      title2: 'Enamorarse.',
      list: [
        {
          name: 'Pistacho',
          copy: 'Coronada con pistacho triturado. Delicada, con notas de fruto seco y elegante.',
          img: 'flavorPistachio',
        },
        {
          name: 'Biscoff',
          copy: 'Cálidas notas de galleta especiada y caramelo sobre nuestra textura de autor. Peligrosamente bueno.',
          img: 'flavorLotus',
        },
        {
          name: 'Oreo',
          copy: 'Un clásico reinventado en cacao. Cremoso, de ensueño e imposible de olvidar.',
          img: 'flavorOreo',
        },
        {
          name: 'Chocolate',
          copy: 'Chocolate profundo y aterciopelado en cada bocado. Pura indulgencia.',
          img: 'flavorChocolate',
        },
        {
          name: 'Manchego',
          copy: 'Lo dulce se encuentra con lo salado de la forma más inesperada e inolvidable.',
          img: 'flavorManchego',
        },
      ],
      footnote:
        'Los cinco sabores se hornean frescos en lotes limitados cada semana. Ordena cuando quieras y recoge los sábados en el mercado.',
    },
    sensory: {
      eyebrow: 'El Empaque',
      title1: 'Hecho para',
      title2: 'Regalar.',
      copy: 'Cada cheesecake llega en una caja redonda hecha a mano, con el sello TARTA en relieve y forrada con esmero. Un empaque pensado para abrirse despacio, porque desempacarlo es parte del regalo.',
      specs: [
        { l: 'Material', v: 'Cartón en relieve' },
        { l: 'Acabado', v: 'Cacao y crema' },
        { l: 'Forma', v: 'Redonda hecha a mano' },
        { l: 'Detalle', v: 'Un pedacito de cielo' },
      ],
      detailNo: 'Detalle Nº 002',
      detailLabel: 'La Caja',
    },
    waitlist: {
      invitation: 'mantente al día',
      title1: 'Sabores y',
      title2: 'Mercados',
      copy: 'Ordena cuando quieras y recoge los sábados en el mercado. Déjanos tus datos para enterarte de los sabores de cada semana, nuevas fechas de mercado y alguna que otra sorpresa, directo a tu correo.',
      benefits: [
        'Sabores de la semana',
        'Nuevas fechas de mercado',
        'Drops sorpresa',
      ],
      labelName: 'Nombre',
      labelEmail: 'Correo',
      labelPhone: 'Teléfono',
      labelCity: 'Ciudad / Zona',
      labelFlavor: 'Sabor Favorito',
      placeholderName: 'Tu nombre',
      placeholderEmail: 'tu@ejemplo.com',
      placeholderPhone: '(opcional)',
      placeholderCity: 'Houston, Memorial…',
      flavorOptions: [
        'Pistacho',
        'Biscoff',
        'Oreo',
        'Chocolate',
        'Manchego',
        'Sorpréndeme',
      ],
      flavorPlaceholder: 'Elige un sabor',
      optional: 'Opcional',
      required: 'Obligatorio',
      invalidEmail: 'Correo inválido',
      submit: 'Mantenme al día',
      footnote:
        'Al registrarte aceptas recibir novedades de TARTA. Respetamos tu privacidad y nunca compartiremos tu información.',
      confirmed: 'Listo',
      welcome: 'Gracias,',
      thanks:
        'Te escribiremos sobre los sabores de cada semana y las fechas de mercado. Mientras tanto, ordena cuando quieras y recoge el sábado en el mercado. Un bocado más cerca del cielo.',
    },
    localLaunch: {
      eyebrow: 'Encuéntranos',
      title1: 'Cada Sábado en',
      title2: 'Houston.',
      copy: 'TARTA está cada sábado en el Memorial Villages Farmers Market de Houston. Ordena en línea cuando quieras y ven a recoger tu cheesecake fresco en el mercado.',
      points: [
        { label: 'Origen', value: 'San Sebastián, España' },
        { label: 'Hecho en', value: 'Houston, Texas' },
        { label: 'Producción', value: 'Lote pequeño' },
        { label: 'Cada sábado', value: 'Memorial Villages Farmers Market' },
      ],
    },
    preorder: {
      eyebrow: 'Ordena y Recoge',
      title1: 'Ordena Cuando Quieras.',
      title2: 'Recoge los Sábados.',
      copy: 'Haz tu pedido en línea cuando quieras y recoge tu cheesecake fresco en el Memorial Villages Farmers Market cada sábado. Lo horneamos en lotes limitados, así que ordena con tiempo para asegurar tu sabor.',
      marketLabel: 'Punto de Recogida',
      marketName: 'Memorial Villages Farmers Market',
      address1: '10840 Beinhorn Rd',
      address2: 'Houston, TX 77024',
      hoursLabel: 'Horario de Recogida',
      hoursDay: 'Sábados',
      hoursTime: '9:00 AM a 1:00 PM',
      cta: 'Recibe novedades',
      note: 'Lotes limitados cada semana. Ordena con tiempo para asegurar tu sabor.',
    },
    instagram: {
      eyebrow: '@tartahtx',
      title1: 'Sigue el',
      title2: 'Primer Capítulo.',
      cta: 'Síguenos @tartahtx',
      posts: ['El primer horneado', 'La caja de autor', 'Un pedacito de cielo'],
    },
    faq: {
      eyebrow: 'Consultas',
      title1: 'Preguntas,',
      title2: 'Respondidas.',
      items: [
        {
          q: '¿Cómo hago mi pedido?',
          a: 'Ordena en línea cuando quieras con el botón Ordena Ahora. Elige tu sabor, haz tu pedido y recógelo fresco en el mercado el sábado.',
        },
        {
          q: '¿Dónde recojo mi pedido?',
          a: 'En el Memorial Villages Farmers Market, 10840 Beinhorn Rd, Houston, TX 77024, cada sábado de 9:00 AM a 1:00 PM.',
        },
        {
          q: '¿Qué sabores ofrecen?',
          a: 'Pistacho, Biscoff, Oreo, Chocolate y Manchego, todos horneados frescos en lotes limitados cada semana.',
        },
        {
          q: '¿Tengo que ordenar con anticipación?',
          a: 'Puedes ordenar en línea cuando quieras, y lo recomendamos para asegurar tu sabor. Los lotes son limitados y los sabores populares se agotan.',
        },
        {
          q: '¿Cómo me mantengo al día?',
          a: 'Regístrate abajo para recibir novedades de sabores y mercados, y síguenos en @tartahtx en Instagram.',
        },
      ],
    },
    finalCTA: {
      edition: 'un pedacito de cielo',
      title1: 'Un Bocado',
      title2: 'Más Cerca',
      title3: 'del Cielo',
      copy1: 'Sabores limitados. Lotes pequeños. Hecho con amor.',
      copy2: 'Ordena ahora y recoge el sábado en el mercado.',
      cta: 'Mantente al día',
    },
    footer: {
      tagline: 'Un pedacito de cielo',
      quote: 'Un bocado más cerca del cielo.',
      origin: 'Dónde',
      originValue: 'Houston, TX',
      originSub: 'Sábados en el Mercado',
      follow: 'Síguenos',
      email: 'tarta@tartahtx.com',
      copyright: 'TARTA. Todos los derechos reservados.',
      crafted: 'Hecho a mano en lotes pequeños en Houston, TX',
      links: ['Privacidad', 'Términos y Condiciones', 'Contacto'],
    },
  },
} as const
